import { useState } from "react";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMentions, jumpToMention, markAllMentionsRead } from "@/estimating/lib/mentions";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const title = (n) => (n.kind === "mention"
  ? `${n.by || n.from} mentioned you on ${n.project_name || "a job"}${n.source === "bod" ? ` · BOD ${n.scope_code}` : n.source === "contract" ? " · Contract" : n.source === "pipeline" ? " · Pipeline" : n.source === "field" ? " · RA notes" : ""}`
  : n.title || `${n.kind} · ${n.project_name || ""}`);

/** Header bell — unread @mentions; a click jumps to the row, whose note icon stays lit until opened. */
export default function MentionsBell() {
  const { items, unread } = useMentions();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Notifications" data-testid="mentions-bell"
                className="relative rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-[#007BFF] px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-white"
                  data-testid="mentions-bell-count">{unread > 99 ? "99+" : unread}</span>)}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="mentions-bell-pop">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Notifications</span>
          {items.length > 0 && (
            <button type="button" onClick={markAllMentionsRead} data-testid="mentions-mark-all-read"
                    className="text-[11px] font-medium text-[#007BFF] hover:underline">Mark all read</button>)}
        </div>
        <ul className="max-h-96 overflow-y-auto py-1">
          {items.map((n) => (
            <li key={n.id}>
              <button type="button" onClick={() => { setOpen(false); jumpToMention(n); }} data-testid={`mentions-item-${n.id}`}
                      className="w-full px-3 py-2 text-left text-xs transition-colors hover:bg-muted">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-foreground">{title(n)}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{when(n.created_at)}</span>
                </div>
                {n.text && <div className="mt-0.5 line-clamp-2 text-muted-foreground">{n.text}</div>}
              </button>
            </li>))}
          {!items.length && <li className="px-3 py-6 text-center text-xs text-muted-foreground" data-testid="mentions-empty">You're all caught up.</li>}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
